
import { Component, Mixins, Watch } from "vue-property-decorator";
import { Line as LineChartGenerator } from 'vue-chartjs';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import AnnotationPlugin from 'chartjs-plugin-annotation';
import AssessmentResult from "@/views/dashboard/assessment-result.vue";
import AssessmentHistory from "@/views/dashboard/assessment-history.vue";
import UtoxResult from "@/views/dashboard/utox-result.vue";
import BiometricResult from "@/views/dashboard/biometric-result.vue";
import DashboardMixin from "./dashboard.mixin";
import BaseMixin from "@/views/base/base.mixin";
import ExpandedPhq9 from "@/views/expanded-dashboard/expanded-phq9.vue";
import ExpandedGad7 from "@/views/expanded-dashboard/expanded-gad7.vue";
import ExpandedSows from "@/views/expanded-dashboard/expanded-sows.vue";
import ExpandedCravings from "@/views/expanded-dashboard/expanded-cravings.vue";
import moment from "moment";
import { Utox } from "./dashboard.types";
import { setChartOptions, getColor } from "./dashboard.utils";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler
} from 'chart.js'
import { SidebarType } from "../sidebar/sidebar.types";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  LinearScale,
  CategoryScale,
  PointElement,
  Filler,
  ChartDataLabels,
  AnnotationPlugin
)

@Component({
  components: {
    LineChartGenerator,
    AssessmentResult,
    AssessmentHistory,
    UtoxResult,
    ExpandedPhq9,
    ExpandedGad7,
    ExpandedSows,
    ExpandedCravings,
    BiometricResult
  }
})
export default class DashboardComponent extends Mixins(BaseMixin, DashboardMixin) {
  selectedUtox = new Utox();
  assessmentResultShow = false;
  assessmentHistoryShow = false;
  assessmentType = "";
  selectedDate = "";
  type?: number;
  assessmentDates = [];
  assessmentIds = [];
  assessmentValues = [];
  alertZones = [];
  isAssessment = true;
  utoxResultShow = false;
  phq9ExpandedShow = false;
  gad7ExpandedShow = false;
  sowsExpandedShow = false;
  cravingsExpandedShow = false;
  biometricResultShow = false;
  loading = false;

  async mounted() {
    this.loading = true;
    const client = await this.waitForClientReady();
    if (client) {
      await this.fetchData();
    } else {
      console.warn("Client not ready in time.");
    }
    this.loading = false;
  }

  beforeDestroy() {
    this.closeMenu();
  }

  get displayDate() {
    const start = this.startDate.format("MMMM D, YYYY");
    const end = this.endDate.format("MMMM D, YYYY");
    return start + " - " + end;
  }

  toRounded(value?: number) {
    if (value == null) {
      return "";
    } else {
      return Math.round(value).toString();
    }
  }

  toMiles(value?: number) {
    if (value == null) {
      return "";
    } else {
      const miles = value / 1609.34;
      return miles.toFixed(2);
    }
  }

  showAppt(index: number) {
    return index <= this.labels.length - 2;
  }

  hasUdsItems(utox: Utox) {
    return utox.udsItems?.length > 0;
  }

  isWeekend(day: string) {
    return day === 'Sa' || day === 'Su';
  }

  async moveTime(forward: boolean) {
    this.loading = true;
    this.pickDate = moment(this.pickDate)
      [forward ? "add" : "subtract"](1, "weeks")
      .toDate();
    await this.fetchData();
    this.loading = false;
  }

  async fetchData() {
    const fetch = [
      this.$store.dispatch("Dashboard/fetchLabels", this.pickDate),
      this.$store.dispatch("Dashboard/fetchUtox", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value
      }),
      this.$store.dispatch("Dashboard/fetchAppointments", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value
      }),
      this.$store.dispatch("Dashboard/fetchBiometricResults", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("Dashboard/fetchPhq9ChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Dashboard/fetchGad7ChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Dashboard/fetchSowsChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Dashboard/fetchCravingsChartOptions", this.selectedClientDDL.value),
      this.$store.dispatch("Dashboard/fetchPhq9ChartData", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("Dashboard/fetchGad7ChartData", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("Dashboard/fetchSowsChartData", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("Dashboard/fetchCravingsChartData", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("Dashboard/fetchBasicNeeds", {
        participantId: this.selectedPatientSummary.participantId, 
        clientId: this.selectedClientDDL.value
      })
    ];
    await Promise.all(fetch);
    setChartOptions(
      this.startDate, 
      this.labels.length, 
      this.phq9AlertZones,
      this.gad7AlertZones,
      this.sowsAlertZones,
      this.cravingsAlertZones,
      this,
      this.$store,
    );
  }

  getPhq9Color(index: number) {
    return { color: getColor(index, this.phq9AlertZones, true) };
  }

  getGad7Color(index: number) {
    return { color: getColor(index, this.gad7AlertZones, true) };
  }

  getSowsColor(index: number) {
    return { color: getColor(index, this.sowsAlertZones, true) };
  }

  getCravingsColor(index: number) {
    return { color: getColor(index, this.cravingsAlertZones, true) };
  }

  getSleepDuration(sleepDuration: string | null): string[] {
    if (!sleepDuration || sleepDuration.trim() === '') {
      return ['', ''];
    }
    return sleepDuration.split(' ');
  }

  showEmojiKey() {
    this.$sidebar.open({
      type: SidebarType.EmojiKey,
      title: "Emoji Key",
      useHistory: true, 
    });
  }

  showNotification() {
    this.$sidebar.open({
      type: SidebarType.SendNotification,
      title: "Send Text, Survey, or Assessment",
      useHistory: true, 
      item: {
        phoneNumber: this.selectedPatientSummary.phoneNumber,
        participantId: this.selectedPatientSummary.participantId,
        patientId: this.selectedPatientSummary.patientId
      },
    });
  }

  showQuestions() {
    this.$sidebar.open({
      type: SidebarType.SurveyQuestions,
      title: "Insights",
      useHistory: true, 
    });
  }

  watchStatistics(): void {
    this.$sidebar.open({
      type: SidebarType.WatchStatistics,
      title: this.selectedPatientSummary.watch ? 
        "Engagement Summary" :
        "Watch Assignment",
      useHistory: true,
      item: { showAvatar: false } 
    });
  }

  editParticipant() {
    this.$sidebar.open({
      type: SidebarType.CommercialParticipant,
      title: "Edit Participant",
      useHistory: true,
      item: { participantId: this.selectedPatientSummary.participantId },
    });
  }

  showUtoxResult(icon: Utox) {
    this.utoxResultShow = true;
    this.selectedUtox = icon;
  }

  showPhq9Expanded() {
    this.phq9ExpandedShow = true;
  }

  showGad7Expanded() {
    this.gad7ExpandedShow = true;
  }

  showSowsExpanded() {
    this.sowsExpandedShow = true;
  }

  showCravingsExpanded() {
    this.cravingsExpandedShow = true;
  }

  showAssessmentHistory(type?: number) {
    this.type = type;
    this.assessmentHistoryShow = true;
  }

  showBiometricResult(index: number) {
    this.biometricResultShow = true;
    this.selectedDate = this.startDate.clone().add(index, 'days').format("M/DD")
  }

  closeMenu() {
    const options = {
      success: false,
      reload: false,
    };
    this.$sidebar.close(options);
  }

  closeDashboard() {
    this.$router.push({ name: 'SummaryComponent'});
  }

  @Watch("selectedClientDDL")
  async onClientChange() {
    this.$router.push({ name: 'SummaryComponent'});
  }

  @Watch("$sidebar.options")
  async onReload() {
    if (this.$sidebar.options?.reload) {
      this.loading = true;
      await this.fetchData();
      this.loading = false;
    }
  }
}
